import { FunctionComponent } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";

import { Sender } from "../Sender";

import { EmailModeSwitch } from "./EmailModeSwitch";
import { SenderRow } from "./SenderRow";
import {
	applyEmailModeToUser,
	applyMasterEmailMode,
	deriveEmailMode,
	deriveMasterEmailMode,
	describeRequirementStatus,
	getUnmetRequirements,
	getUserCapabilities,
	groupSendersByUser,
	isUserEligible,
	isLinkedinActive,
	isUserSelected,
	resolveLinkedinTier,
	setAllUsersSelected,
	setUserSelected,
	toggleEmailActive,
	toggleLinkedinActive,
} from "./senderSelector.helpers";
import { CampaignStepChannels, EmailMode } from "./senderSelector.types";

type SelectableMode = Exclude<EmailMode, "custom">;

/**
 * The campaign senders table (PREC-3564). Lists every sending user with a Primary/Secondary/Both
 * email switch, expandable per-inbox toggles, and a master bulk control. When `steps` is provided
 * (Edit, where the campaign already has a sequence) users that can't perform a required step are
 * greyed out with an explanation; Create passes no `steps`, so every user is selectable.
 */
export const SenderSelector: FunctionComponent<{
	senders: Sender[];
	sendingUsers: User[];
	setSenders: (senders: Sender[]) => void;
	error?: boolean;
	steps?: CampaignStepChannels;
}> = ({ senders, sendingUsers, setSenders, error, steps }) => {
	const rows = groupSendersByUser(senders, sendingUsers);

	const decorated = rows.map((row) => {
		const capabilities = getUserCapabilities(row);

		return {
			row,
			eligible: isUserEligible(capabilities, steps),
			unmet: getUnmetRequirements(capabilities, steps),
			tier: resolveLinkedinTier(row),
			mode: deriveEmailMode(row),
			selected: isUserSelected(row),
			linkedinActive: isLinkedinActive(row),
		};
	});

	const eligibleRows = decorated.filter((entry) => entry.eligible);
	const eligibleIds = new Set(eligibleRows.map((entry) => entry.row.userId));
	const allSelected = eligibleRows.length > 0 && eligibleRows.every((entry) => entry.selected);
	const someSelected = eligibleRows.some((entry) => entry.selected);

	const selectedEligibleRows = eligibleRows
		.filter((entry) => entry.selected)
		.map((entry) => entry.row);
	const masterMode = deriveMasterEmailMode(
		selectedEligibleRows.filter((row) => row.emailSenders.length > 0)
	);

	const handleMasterMode = (mode: SelectableMode) => {
		const targetIds = new Set(selectedEligibleRows.map((row) => row.userId));

		setSenders(applyMasterEmailMode(senders, rows, targetIds, mode));
	};

	const handleConnectEmails = () => window.open(ROUTE_CONFIG.ACCOUNT, "_blank", "noopener");

	const requirements = describeRequirementStatus(rows, steps);

	return (
		<div className="flex flex-col">
			{requirements.length > 0 && (
				<div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-blue-action-200/40 px-3 py-2">
					<Icon className="h-3.5 w-3.5 text-blue-action" icon={faCircleInfo} />
					<span className="text-xs font-medium text-text-meta">This campaign requires:</span>
					{requirements.map((requirement) => (
						<span
							key={requirement.label}
							className={classNames(
								"rounded-md px-2 py-0.5 text-xs font-semibold",
								requirement.met
									? "bg-white text-blue-action"
									: "bg-status-error-light text-status-error"
							)}
							data-met={requirement.met ? "true" : "false"}
							title={requirement.met ? undefined : "No selected sender covers this yet"}
						>
							{requirement.label}
						</span>
					))}
				</div>
			)}
			{/* `flex-wrap` for the same reason as the per-user rows: the label and the mode switch each
			    have a minimum width, so a narrow container needs the switch to drop to its own line
			    rather than the row overflowing. No effect once there is room. */}
			<div className="flex flex-wrap items-center gap-3 border-b border-border-light pb-3">
				<div className="w-5 shrink-0">
					<Checkbox
						error={error}
						isChecked={allSelected}
						isDisabled={eligibleRows.length === 0}
						isIndeterminate={!allSelected && someSelected}
						showError={!!error}
						onChange={() => setSenders(setAllUsersSelected(senders, eligibleIds, !allSelected))}
					/>
				</div>
				<span className="text-sm font-semibold text-gray-900">All sending users</span>
				<span className="flex-1" />
				<div className="ml-8 flex items-center gap-3">
					<span className="text-xs font-medium text-text-meta">
						{masterMode === "custom" ? "Mixed" : "Use emails"}
					</span>
					<EmailModeSwitch disabled={!someSelected} mode={masterMode} onChange={handleMasterMode} />
				</div>
			</div>

			<div className="flex flex-col">
				{decorated.map((entry) => (
					<SenderRow
						key={entry.row.userId}
						eligible={entry.eligible}
						error={error}
						mode={entry.mode}
						row={entry.row}
						selected={entry.selected}
						tier={entry.tier}
						unmet={entry.unmet}
						onConnectEmails={handleConnectEmails}
						onModeChange={(mode) =>
							setSenders(applyEmailModeToUser(senders, entry.row.userId, mode))
						}
						linkedinActive={entry.linkedinActive}
						onToggleEmail={(uuid) => setSenders(toggleEmailActive(senders, uuid))}
						onToggleLinkedin={() => setSenders(toggleLinkedinActive(senders, entry.row.userId))}
						onToggleSelected={() =>
							setSenders(setUserSelected(senders, entry.row.userId, !entry.selected))
						}
					/>
				))}

				{decorated.length === 0 && (
					<div
						className={classNames(
							"flex flex-wrap items-center gap-2 py-4 text-sm",
							error ? "text-status-error" : "text-text-meta"
						)}
					>
						<span>No sending users available.</span>
						<button
							className="cursor-pointer font-semibold text-brand-light hover:underline"
							type="button"
							onClick={handleConnectEmails}
						>
							Connect an account
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
